import type {
  Book,
  MapCluster,
  ReaderTrack,
  Settings,
  TrackDraft,
  TrackFigures,
  TrackPlan,
} from '@wellread/api';
import { count, plural } from '@wellread/core';

// A track is a shelf with a purpose. These are the words the app has for
// one: what its plan does, and how it is going.

export type Marking = TrackPlan['marking'];

export const MARKINGS: readonly { value: Marking; label: string }[] = [
  { value: 'free', label: 'I write my answer, and AI grades it' },
  { value: 'choice', label: 'I pick from four answers' },
  { value: 'two', label: 'I mark myself: forgot or recalled' },
  { value: 'four', label: 'I mark myself: again, hard, good or easy' },
];

const marked: Record<Marking, string> = {
  free: 'You write your answer and AI grades it.',
  choice: 'You pick from four answers.',
  two: 'You mark yourself: forgot or recalled.',
  four: 'You mark yourself: again, hard, good or easy.',
};

/** What a plan does with the books on it, in a line. */
export function planLine(p: TrackPlan): string {
  if (p.capture === 'story') return 'Keeps the cast and the story so far. No questions.';
  return p.recall === 'off'
    ? 'Keeps the ideas. No questions.'
    : `Keeps the ideas and asks you about them ${often[frequencyOf(p.target)]}. ${marked[p.marking]}`;
}

/** The plans of the two ready-made tracks, as the server makes them. */
export const STUDY: TrackPlan = { capture: 'ideas', recall: 'practise', marking: 'free', target: 0.9 };
export const STORIES: TrackPlan = { capture: 'story', recall: 'off', marking: 'free', target: 0.9 };

/** A track as it is before it is made: its name, what it is for, and its plan. */
export type NewTrack = { name: string; goal: string; plan: TrackPlan };

/**
 * The ready-made tracks a reader can start with: each a name, a line about
 * what it does, and the plan behind it. Taking one makes a track of the
 * reader's own, to change as they like. Only the two every reader knows the
 * shape of; anything else is said in the conversation that makes a track.
 */
export const READY_MADE: readonly { key: string; name: string; line: string; plan: TrackPlan }[] = [
  {
    key: 'study',
    name: 'Study',
    line: 'Ideas, a map, and questions over time.',
    plan: STUDY,
  },
  {
    key: 'stories',
    name: 'Stories',
    line: 'For novels: the cast and the story so far.',
    plan: STORIES,
  },
];

/** What a reader said they read for, at first run: an exam, a change of job, for its own sake, or not said. */
export type Purpose = Settings['profile']['purpose'];

/** A track's name and what it is for, as the server keeps them: spaces run together, and no longer than it takes. */
const clean = (s: string, max: number) => [...s.trim().replace(/\s+/g, ' ')].slice(0, max).join('').trim();

/**
 * The Study track first run starts a reader on who reads for an exam or a
 * change of job: named for the exam or the work when they said which, and
 * for that. Anyone else gets Study as it is.
 */
export function studyFor(purpose: Purpose, said: string): NewTrack {
  const what = clean(said, 60);
  const name = what ? what[0]!.toUpperCase() + what.slice(1) : 'Study';
  if (purpose === 'exam') {
    return { name, goal: what ? `Preparing for ${what}` : 'Preparing for an exam', plan: STUDY };
  }
  if (purpose === 'job') {
    return {
      name,
      goal: what ? `Preparing for work in ${what}` : 'Preparing for a change of job',
      plan: STUDY,
    };
  }
  return { name: 'Study', goal: '', plan: STUDY };
}

/**
 * The line a track's books are asked for in (Explore's ask, 300 characters
 * at most): what it is for, in the reader's words. A track that doesn't say
 * is asked for by its name; the ask knows the reader's library besides.
 */
export function booksAsk(t: Pick<NewTrack, 'name' | 'goal' | 'plan'>): string {
  const line = t.goal
    ? `Books for ${t.name}: ${t.goal}`
    : `${t.plan.capture === 'story' ? 'Novels' : 'Books'} for my ${t.name} track`;
  return clean(line, 300);
}

/**
 * How often a track asks about its ideas. Behind each is the recall the
 * ideas are held to: the higher it is, the sooner an idea comes back.
 */
export const TARGETS: readonly { value: Frequency; label: string; target: number }[] = [
  { value: 'sometimes', label: 'Now and then', target: 0.8 },
  { value: 'regularly', label: 'Regularly', target: 0.9 },
  { value: 'often', label: 'Often', target: 0.95 },
];
export type Frequency = 'sometimes' | 'regularly' | 'often';

const often: Record<Frequency, string> = {
  sometimes: 'now and then',
  regularly: 'regularly',
  often: 'often',
};

/** The choice a stored target stands for: the nearest of the three. */
export function frequencyOf(target: number): Frequency {
  return TARGETS.reduce((best, t) =>
    Math.abs(t.target - target) < Math.abs(best.target - target) ? t : best,
  ).value;
}
export const targetOf = (f: Frequency): number => TARGETS.find((t) => t.value === f)!.target;

/** The kind of book a plan's capture asks for, in the words a book carries. */
export const kindOf = (p: TrackPlan): Book['track'] => (p.capture === 'story' ? 'narrative' : 'technical');

/** How a track is going, as the facts of one line; nothing that isn't switched on. */
export function trackFacts(t: { plan: TrackPlan; figures: TrackFigures }): string[] {
  const f = t.figures;
  const facts = [plural(f.books, 'book')];
  if (!f.books) return facts;
  facts.push(`${f.read_percent}% read`);
  if (t.plan.capture === 'ideas' && f.ideas) {
    facts.push(`${f.held_percent}% held`);
    if (t.plan.recall !== 'off' && f.due) facts.push(`${count(f.due)} due`);
  }
  return facts;
}

/**
 * True while the reader has only the two ready-made tracks, as made: the
 * library then looks as it did before tracks.
 */
export function untouched(tracks: readonly ReaderTrack[]): boolean {
  return tracks.every(
    (t) =>
      t.preset !== 'own' &&
      !t.goal &&
      samePlan(t.plan, t.preset === 'stories' ? STORIES : STUDY, t.preset === 'study'),
  );
}

// The ready-made Study track takes the marking the reader had chosen
// before tracks, so its marking alone doesn't make it touched.
const samePlan = (a: TrackPlan, b: TrackPlan, anyMarking = false) =>
  a.capture === b.capture && a.recall === b.recall && (anyMarking || a.marking === b.marking);

/** The tracks a book could also go on: those that capture what it was analysed for, and don't hold it yet. */
export const tracksFor = (book: Book, tracks: readonly ReaderTrack[]) =>
  tracks.filter((t) => kindOf(t.plan) === book.track && !book.tracks.includes(t.id));

/**
 * The tracks that keep something else of a book (a story's cast, where it
 * was read for ideas). A book can only move to one whole, off the one
 * track it is on, and is analysed again when it does.
 */
export const tracksToMoveTo = (book: Book, tracks: readonly ReaderTrack[]) =>
  book.tracks.length === 1 ? tracks.filter((t) => kindOf(t.plan) !== book.track) : [];

/**
 * A track as the conversation that changes it starts: what its plan says,
 * in the words a draft uses. How often is "never" for a track that asks
 * nothing, and then how answers are given isn't settled.
 */
export function draftOf(t: Pick<ReaderTrack, 'name' | 'goal' | 'plan'>): TrackDraft {
  const asks = t.plan.capture === 'ideas' && t.plan.recall !== 'off';
  return {
    name: t.name,
    goal: t.goal,
    capture: t.plan.capture,
    asked: t.plan.capture === 'story' ? '' : asks ? frequencyOf(t.plan.target) : 'never',
    marking: asks ? t.plan.marking : '',
  };
}

/**
 * The themes of a track's map the reader holds least of, weakest first: up
 * to three, among those with something still to hold. With fewer than two
 * themes there is nothing to compare.
 */
export function weakestThemes(clusters: readonly MapCluster[], limit = 3): MapCluster[] {
  if (clusters.length < 2) return [];
  return clusters
    .filter((c) => c.idea_count > 0 && c.due + c.missed + c.unseen > 0)
    .sort(
      (a, b) =>
        a.mastered_percent - b.mastered_percent ||
        // What a review would ask now: the number a theme prints as due.
        b.review_now - a.review_now ||
        a.name.localeCompare(b.name),
    )
    .slice(0, limit);
}
